import api from './api';

const KEY = 'aas_attribution';
const SESSION_KEY = 'aas_session_id';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

// Click ids → paid channel (no utm_* needed)
const CLICK_IDS = {
  gclid: ['google', 'cpc'], gbraid: ['google', 'cpc'], wbraid: ['google', 'cpc'],
  msclkid: ['bing', 'cpc'], fbclid: ['facebook', 'paid_social'], ttclid: ['tiktok', 'paid_social'],
  epik: ['pinterest', 'paid_social'], sccid: ['snapchat', 'paid_social'], rdt_cid: ['reddit', 'paid_social'],
};
const SEARCH_ENGINES = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'ecosia.', 'baidu.', 'yandex.', 'brave.', 'startpage.', 'ask.'];
const SOCIAL = {
  'pinterest.': 'pinterest', 'facebook.': 'facebook', 'fb.com': 'facebook', 'instagram.': 'instagram', 'tiktok.': 'tiktok',
  'reddit.': 'reddit', 'twitter.': 'twitter', 'x.com': 'twitter', 't.co': 'twitter', 'youtube.': 'youtube', 'youtu.be': 'youtube',
  'snapchat.': 'snapchat', 'threads.': 'threads', 'linkedin.': 'linkedin', 'twitch.': 'twitch', 'discord.': 'discord',
};

export const sessionId = () => {
  let id = localStorage.getItem(SESSION_KEY);
  if (!id) {
    id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
    localStorage.setItem(SESSION_KEY, id);
  }
  return id;
};

// Respect automation clients and Global Privacy Control / Do Not Track signals
export const trackingAllowed = () => {
  if (typeof navigator === 'undefined') return false;
  if (navigator.webdriver) return false;
  if (navigator.globalPrivacyControl === true) return false;
  return navigator.doNotTrack !== '1' && window.doNotTrack !== '1';
};

const referrerHost = (ref) => {
  try { return ref ? new URL(ref).hostname.replace(/^www\./, '').toLowerCase() : ''; } catch { return ''; }
};

// Classify a non-UTM arrival: { utm_source, utm_medium, channel } or null for internal navigation
export const inferChannel = (search, referrer, ownHost) => {
  const params = new URLSearchParams(search);
  for (const [id, [source, medium]] of Object.entries(CLICK_IDS)) {
    if (params.get(id)) return { utm_source: source, utm_medium: medium, channel: 'paid' };
  }
  const host = referrerHost(referrer);
  if (host && ownHost && (host === ownHost.replace(/^www\./, '').toLowerCase())) return null;
  if (!host) return { utm_source: 'direct', utm_medium: 'none', channel: 'direct' };
  if (SEARCH_ENGINES.some((e) => host === e.slice(0, -1) || host.includes(e))) {
    return { utm_source: host.split('.')[0], utm_medium: 'organic', channel: 'organic' };
  }
  for (const [frag, name] of Object.entries(SOCIAL)) {
    if (host === frag || host.includes(frag)) return { utm_source: name, utm_medium: 'social', channel: 'social' };
  }
  return { utm_source: host.slice(0, 120), utm_medium: 'referral', channel: 'referral' };
};

// UTM arrivals always win and overwrite; inferred arrivals are first-touch only (never replace a valid record)
export const captureAttribution = (search, referrer = document.referrer, ownHost = window.location.hostname) => {
  const params = new URLSearchParams(search);
  const found = {};
  UTM_KEYS.forEach((k) => { const v = params.get(k); if (v) found[k] = v.slice(0, 120); });
  const base = { landed_at: Date.now(), landing_path: window.location.pathname, referrer_host: referrerHost(referrer) };
  if (Object.keys(found).length) {
    const record = { ...found, ...base, kind: 'utm', channel: 'campaign' };
    localStorage.setItem(KEY, JSON.stringify(record));
    return record;
  }
  if (getAttribution()) return null;
  const inferred = inferChannel(search, referrer, ownHost);
  if (!inferred) return null;
  const record = { ...inferred, ...base, kind: 'inferred' };
  localStorage.setItem(KEY, JSON.stringify(record));
  return record;
};

export const getAttribution = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw);
    if (!rec.landed_at || Date.now() - rec.landed_at > TTL_MS) { localStorage.removeItem(KEY); return null; }
    return rec;
  } catch { return null; }
};

export const attributionPayload = () => {
  const rec = getAttribution();
  if (!rec) return undefined;
  const out = {};
  UTM_KEYS.forEach((k) => { if (rec[k]) out[k] = rec[k]; });
  return out;
};

export const trackAttributionEvent = async (type, productId = null, value = null) => {
  if (!trackingAllowed()) return;
  const rec = getAttribution();
  const attribution = attributionPayload();
  if (!attribution) return;
  try {
    await api.post('/attribution/event', {
      type, session_id: sessionId(), attribution, product_id: productId, value,
      channel: rec?.channel || null, referrer_host: rec?.referrer_host || null, landing_path: rec?.landing_path || null,
    });
  } catch { /* never block UX */ }
};

export const newEventId = (prefix) =>
  `${prefix}_${window.crypto?.randomUUID?.() || `${Date.now()}${Math.random().toString(36).slice(2, 10)}`}`;

// Fire-and-forget server-side (Conversions API) event; shares event_id with the browser pixel for dedupe
export const trackServerEvent = (path, params) => {
  if (!trackingAllowed()) return;
  api.post(path, null, { params }).catch(() => {});
};
